import { ChatHistoryType } from '@/types/converse/chatHistory';
import {
  KeyboardDoubleArrowLeftRounded,
  KeyboardDoubleArrowRightRounded,
} from '@mui/icons-material';
import { Box, Drawer, IconButton, useTheme, Container } from '@mui/material';
import React, { useState, useEffect, ReactElement } from 'react';
import CallTranscript from './CallTranscript';

interface CallsSideSheetProps {
  children: React.ReactNode;
  panelWidth: string;
  selectedCallId: string | null;
  transcript: ChatHistoryType['messages']; // Using TranscriptMessage type instead of any
  loading?: boolean;
  forceOpen?: boolean;
  defaultMaxWidth?: 'xl' | 'lg' | 'md' | 'sm' | false;
  openMaxWidth?: 'xl' | 'lg' | 'md' | 'sm' | false;
}

const CallsSideSheet: React.FC<CallsSideSheetProps> = ({
  children,
  panelWidth,
  selectedCallId,
  transcript,
  loading = false,
  forceOpen = false,
  defaultMaxWidth = 'xl',
  openMaxWidth = 'lg',
}) => {
  const [open, setOpen] = useState<boolean>((transcript ?? []).length > 0);
  const theme = useTheme();
  const currentTheme = theme.palette.mode;

  // Update open state when forceOpen prop changes and when a new transcript is selected
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  useEffect(() => {
    if ((transcript ?? []).length > 0 && selectedCallId) {
      setOpen(true);
    }
  }, [selectedCallId, transcript]);

  console.log('Transcripts received at CallsSideSheet = ');
  console.log(transcript);

  // Modify children to pass the correct maxWidth
  const modifiedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement<{maxWidth?: 'xl' | 'lg' | 'md' | 'sm' | false}>(child) && child.type === Container) {
      return React.cloneElement(child, {
        maxWidth: open ? openMaxWidth : defaultMaxWidth,
      });
    }
    return child;
  });

  return (
    <>
      <Drawer
        anchor="right"
        open
        sx={{
          overflowX: 'hidden',
          width: open ? panelWidth : 40,
          border: 'none',
          transition: 'all 0.3s',
          flexShrink: 0,
          '& .MuiDrawer-paperAnchorRight': {
            border: 'none',
            width: open ? panelWidth : 40,
          },
          '& .MuiDrawer-paper': {
            width: open ? panelWidth : 40,
            backgroundColor: 'transparent',
          },
        }}
        variant="persistent"
      >
        <Box
          sx={{
            display: 'flex',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: 'transparent',
            }}
          >
            <IconButton
              onClick={() => {
                setOpen(!open);
              }}
              sx={{
                cursor: 'pointer',
                color: 'white',
                borderRadius: '10px 0 0 10px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '0.2rem',
              }}
            >
              {open ? (
                <KeyboardDoubleArrowRightRounded sx={{ fontSize: '2rem' }} />
              ) : (
                <KeyboardDoubleArrowLeftRounded sx={{ fontSize: '2rem' }} />
              )}
            </IconButton>
          </Box>

          {/* Transcript Panel */}
          {open && (
            <Box
              sx={{
                width: panelWidth,
                typography: 'caption',
                overflowX: 'hidden',
                border: '3px solid',
                borderRadius: 2,
                background: 
                  currentTheme === 'dark'
                    ? 'linear-gradient(320deg, #000000 0%, #0A0A0A 100%)'
                    : 'linear-gradient(320deg, #ff9a3520 1.52%, #00000000 90.82%)',
                borderImageSource:
                  'linear-gradient(270deg, #00000000 1.52%, #5A5A5A20 90.98%)',
                borderImageSlice: 1,
                height: '100%',
                opacity: '100%',
              }}
            >
              <CallTranscript
                callId={selectedCallId}
                transcript={transcript}
                loading={loading}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      <Box
        sx={{
          marginRight: open ? panelWidth : 0,
          maxWidth: open ? `calc(100% - ${panelWidth})` : '100%',
          flex: 1,
          transition: 'all 0.3s',
        }}
      >
        {modifiedChildren}
      </Box>
    </>
  );
};

export default CallsSideSheet;
